// XSM-1536 - SSO Login flow
// Author: Abhishek Bharti
import { useState } from "react";
import { Grid, InputLabel, Typography, useMediaQuery } from "@mui/material";
import { FormInput } from "gf-ui-components-mui-lib";
import { useTheme } from "@mui/material";
import { IoEye, IoEyeOff } from "react-icons/io5";

interface ICommonInputWrapper {
  htmlFor?: string;
  label?: string;
  placeholder?: string;
  name: string;
  type?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  styles?: any;
  disabled?: boolean;
  handleBlur?: any;
  handleFocus?: any;
  leftPanel?: any;
  setIsFocusFields?: any;
  isFocusedFields?: any;
  value?: any;
  endIcon?: any;
  dots?: any;
  popupCountryWidth?: string;
  popupPhoneWidth?: string;
  show?: boolean;
  endIconClick?: any;
  errorStyles?: any;
  isLabelButton?: boolean;
  labelButton?: any;
  customError?: any;
  width?: string;
  height?: string;
  isOTPInput?: boolean;
  forwardRef?: any;
  onKeyUp?: any;
  onKeyDown?: any;
  maxLength?: any;
  handlePaste?: any;
  validationId?: string;
  eyeIconDataId?: string;
  autoFocus?: boolean;
  error?: string;
}

const CommonInputWrapper = (props: ICommonInputWrapper) => {
  const { maxLength = 200, handlePaste, value = "", error } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xl"));
  const [show, setShow] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const formInputStyles = {
    input: {
      "&::placeholder": {
        textOverflow: "ellipsis !important",
        color: "#667085",
        fontFamily: "Poppins",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "24px",
        opacity: 1,
      },
    },
    "& .MuiOutlinedInput-root": {
      padding: "0px 14px !important",
      height: "44px !important",
      borderRadius: "8px",
      boxShadow:
        "0px 0.9999999403953552px 1.9999998807907104px 0px rgba(16, 24, 40, 0.05)",
      backgroundColor: props.disabled
        ? "var(--gray-050, #FAFAFA)"
        : "var(--gray-white, #FFF)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border:
        error || props.customError ? "1px solid #F27A7A" : "1px solid #D0D5DD",
    },
    "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      border:
        error || props.customError ? "1px solid #F27A7A" : "1px solid #D0D5DD",
      boxShadow: "none",
      padding: 0,
      paddingBottom: props.dots && !props.show ? "5px" : "1.5px",
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      border:
        error || props.customError ? "1px solid #F27A7A" : "1px solid #D0D5DD",
    },
    "& .MuiInputBase-input": {
      height: "22px",
      backgroundColor: "inherit",
      color:
        props.dots && props.type === "password"
          ? "#333"
          : props.disabled
          ? "#667085"
          : "#000",
      fontFamily: "Poppins",
      fontWeight: 400,
      fontSize: props.dots && !props.show ? "35px" : "16px",
      lineHeight: "22px",
      padding: 0,
      paddingBottom: props.dots && !props.show ? "5.3px" : "1.5px",
      ...(props.isOTPInput ? { textAlign: "center" } : null),
    },
    "& .MuiInputAdornment-root": { paddingLeft: "0px", margin: "0px" },
  };

  const otpStyles = {
    width: "48.272px",
    height: "48.272px",
  };

  const inputLabelStyles = {
    fontFamily: "Poppins",
    fontWeight: 500,
    fontSize: isSmallScreen ? "12px" : "14px",
    lineHeight: "20px",
    color: "#344054",
  };

  const handleBlur = (e: any) => {
    props?.handleBlur?.(props.label);
  };

  const handleChange = (e: any) => {
    if (e.target.value.length <= maxLength) {
      setInputValue(e.target.value);
      props?.onChange?.(e);
    }
  };

  const handleFocus = () => {
    props?.handleFocus?.(props.label);
  };

  return (
    <Grid
      item
      container
      sx={{
        pointerEvents: props.disabled ? "none" : "all",
        gap: "6px",
        ...(props.isOTPInput ? otpStyles : {}),
      }}
    >
      {props.label && (
        <Grid
          item
          container
          sx={{
            justifyContent: "space-between",
          }}
        >
          <InputLabel sx={inputLabelStyles} htmlFor={props.name}>
            {props.label}
          </InputLabel>
          {props.isLabelButton && props.labelButton}
        </Grid>
      )}
      <Grid item xs={12}>
        <FormInput
          sx={{ ...formInputStyles, ...(props.isOTPInput ? otpStyles : {}) }}
          className={
            error || props.customError ? "error-border" : "normal-border"
          }
          autoFocus={props.autoFocus ? true : undefined}
          {...{
            value: props.value !== undefined ? props.value : inputValue,
            onChange: handleChange,
            placeholder: props?.placeholder,
            forwardRef: props.forwardRef,
            onKeyUp: props.onKeyUp,
            onKeyDown: props.onKeyDown,
            maxLength: props.maxLength,
            handlePaste,
            id: props.name,
            name: props.name,
            type:
              show || props.type !== "password"
                ? props.type === "number"
                  ? "number"
                  : "text"
                : "password",
            onFocus: handleFocus,
            onBlur: handleBlur,
            endIcon: props.endIcon ? (
              props.endIcon
            ) : props.type === "password" ? (
              !show && !props.show ? (
                <IoEye
                  cursor={"pointer"}
                  size={18}
                  onClick={() => {
                    setShow(!show);
                  }}
                  data-testid={props.eyeIconDataId}
                />
              ) : (
                <IoEyeOff
                  data-testid={props.eyeIconDataId}
                  size={18}
                  cursor={"pointer"}
                  onClick={() => setShow(!show)}
                />
              )
            ) : null,
            ...(props.isOTPInput
              ? {
                  min: 0,
                  max: 9,
                  maxLength: 1,
                }
              : null),
          }}
        />
        {(error && !props.disabled) || props.customError ? (
          <Typography
            sx={{
              fontSize: "10px",
              color: "#F27A7A",
              fontFamily: "Poppins",
              lineHeight: "16px",
            }}
            data-testid={props.validationId}
          >
            {error || props.customError}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: "10px",
              color: "transparent",
              fontFamily: "Poppins",
              lineHeight: "16px",
              ...props.errorStyles,
            }}
          >
            &nbsp;&nbsp;
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

export default CommonInputWrapper;
